// 产品矩阵系统 - 导出弹窗组件
// 参考 Canva/美图秀秀 的现代设计风格
import { useState } from 'react';
import { X, Download, Image as ImageIcon, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { toPng, toJpeg } from 'html-to-image';
import { useCanvasStore } from '../stores/canvas-store';
import type { ExportFormat } from '../types';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type WatermarkPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

interface ExportOptions {
  format: ExportFormat;
  quality: number;
  scale: number;
  includeWatermark: boolean;
  watermarkText: string;
  watermarkPosition: WatermarkPosition;
  watermarkOpacity: number;
}

export function ExportModal({ isOpen, onClose }: ExportModalProps) {
  const { layoutType, getFilledCellCount, customRows, customCols, showCanvasBackground, canvasBackgroundColor } = useCanvasStore();
  
  const [options, setOptions] = useState<ExportOptions>({
    format: 'png',
    quality: 90,
    scale: 2,
    includeWatermark: false,
    watermarkText: 'LED Display Solutions',
    watermarkPosition: 'bottom-right',
    watermarkOpacity: 30,
  });
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportedUrl, setExportedUrl] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setExportedUrl(null);
    setExportError(null);
    setExportStatus('正在准备导出...');

    try {
      const canvasElement = document.querySelector('[data-canvas-export]') as HTMLElement;
      const gridElement = document.querySelector('.grid') as HTMLElement;
      
      if (!canvasElement && !gridElement) {
        throw new Error('Canvas element not found');
      }

      const targetElement = canvasElement || gridElement;

      setExportStatus('正在等待图片加载...');
      const images = targetElement.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise<void>((resolve) => {
            if (img.complete && img.naturalHeight !== 0) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
              setTimeout(() => resolve(), 5000);
            }
          });
        })
      );

      await new Promise(resolve => setTimeout(resolve, 300));

      setExportStatus('正在生成图片...');

      const rect = targetElement.getBoundingClientRect();
      
      const exportOptions = {
        quality: options.quality / 100,
        pixelRatio: options.scale,
        // 直接使用左侧面板设置的背景色
        backgroundColor: showCanvasBackground ? canvasBackgroundColor : '#ffffff',
        width: rect.width,
        height: rect.height,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
        },
        filter: (node: HTMLElement) => {
          // 过滤掉删除按钮
          if (node.tagName === 'BUTTON' && node.classList.contains('opacity-0')) {
            return false;
          }
          // 过滤掉空单元格的占位内容
          if (node.hasAttribute && node.hasAttribute('data-export-hide')) {
            return false;
          }
          return true;
        },
        fetchRequestInit: {
          mode: 'cors' as RequestMode,
          credentials: 'omit' as RequestCredentials,
        },
        skipFonts: false,
        cacheBust: true,
      };

      let dataUrl: string;

      if (options.format === 'png') {
        dataUrl = await toPng(targetElement, exportOptions);
      } else if (options.format === 'jpg') {
        dataUrl = await toJpeg(targetElement, {
          ...exportOptions,
          quality: options.quality / 100,
        });
      } else {
        dataUrl = await toPng(targetElement, exportOptions);
      }

      if (options.includeWatermark && options.watermarkText) {
        setExportStatus('正在添加水印...');
        dataUrl = await addWatermark(dataUrl, options.watermarkText, options.watermarkPosition, options.watermarkOpacity);
      }

      if (options.format === 'webp') {
        setExportStatus('正在转换格式...');
        dataUrl = await convertToWebP(dataUrl, options.quality / 100);
      }

      setExportedUrl(dataUrl);
      setExportStatus('');
      
    } catch (error) {
      console.error('Export error:', error);
      const errMsg = error instanceof Error ? error.message : '未知错误';
      setExportError(`导出失败: ${errMsg}`);
      setExportStatus('');
    } finally {
      setIsExporting(false);
    }
  };

  const addWatermark = (
    dataUrl: string, 
    text: string, 
    position: WatermarkPosition,
    opacity: number
  ): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          
          // 计算字体大小和边距
          const fontSize = Math.max(14, Math.min(24, img.width / 40));
          const padding = Math.max(16, img.width / 40);
          
          ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity / 100})`;
          
          // 添加轻微阴影增强可读性
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // 根据位置设置文字对齐和坐标
          const textMetrics = ctx.measureText(text);
          let x: number, y: number;
          
          switch (position) {
            case 'top-left':
              ctx.textAlign = 'left';
              x = padding;
              y = padding + fontSize;
              break;
            case 'top-right':
              ctx.textAlign = 'right';
              x = img.width - padding;
              y = padding + fontSize;
              break;
            case 'bottom-left':
              ctx.textAlign = 'left';
              x = padding;
              y = img.height - padding;
              break;
            case 'bottom-right':
            default:
              ctx.textAlign = 'right';
              x = img.width - padding;
              y = img.height - padding;
              break;
          }
          
          ctx.fillText(text, x, y);
          resolve(canvas.toDataURL('image/png'));
        } else {
          resolve(dataUrl);
        }
      };
      img.src = dataUrl;
    });
  };

  const convertToWebP = (dataUrl: string, quality: number): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/webp', quality));
        } else {
          resolve(dataUrl);
        }
      };
      img.src = dataUrl;
    });
  };

  const handleDownload = () => {
    if (!exportedUrl) return;

    const link = document.createElement('a');
    link.href = exportedUrl;
    link.download = `led-poster-${layoutType}-${Date.now()}.${options.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const layoutLabel = layoutType === 'grid_custom' 
    ? `${customRows}×${customCols}` 
    : layoutType.replace('grid_', '').replace('x', '×');

  // 格式选项
  const formatOptions: { value: ExportFormat; label: string; desc: string }[] = [
    { value: 'png', label: 'PNG', desc: '无损' },
    { value: 'jpg', label: 'JPG', desc: '较小' },
    { value: 'webp', label: 'WebP', desc: '最小' },
  ];

  // 分辨率选项
  const scaleOptions = [
    { value: 1, label: '1x', desc: '标准' },
    { value: 2, label: '2x', desc: '高清', recommended: true },
    { value: 3, label: '3x', desc: '超清' },
  ];

  // 水印位置选项
  const positionOptions: { value: WatermarkPosition; label: string }[] = [
    { value: 'bottom-right', label: '右下' },
    { value: 'bottom-left', label: '左下' },
    { value: 'top-right', label: '右上' },
    { value: 'top-left', label: '左上' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">导出海报</h2>
              <p className="text-sm text-gray-500">{layoutLabel} 布局 · {getFilledCellCount()} 个产品</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col md:flex-row min-h-full">
            {/* 左侧：导出选项 */}
            <div className="w-full md:w-72 p-5 border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 space-y-5">
              
              {/* 格式选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">格式</label>
                <div className="grid grid-cols-3 gap-2">
                  {formatOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setOptions((prev) => ({ ...prev, format: opt.value }))}
                      className={`relative py-2.5 px-3 rounded-xl text-center transition-all ${
                        options.format === opt.value
                          ? 'bg-blue-50 border-2 border-blue-500 text-blue-700'
                          : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-semibold text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 质量滑块 (JPG/WebP) */}
              {options.format !== 'png' && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-900">质量</label>
                    <span className="text-sm text-blue-600 font-medium">{options.quality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={options.quality}
                    onChange={(e) => setOptions((prev) => ({ ...prev, quality: Number(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                </div>
              )}

              {/* 分辨率选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">分辨率</label>
                <div className="grid grid-cols-3 gap-2">
                  {scaleOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setOptions((prev) => ({ ...prev, scale: opt.value }))}
                      className={`relative py-2.5 px-3 rounded-xl text-center transition-all ${
                        options.scale === opt.value
                          ? 'bg-blue-50 border-2 border-blue-500 text-blue-700'
                          : 'bg-gray-50 border-2 border-transparent text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {opt.recommended && (
                        <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full">
                          <Sparkles className="w-2.5 h-2.5 text-white" />
                        </span>
                      )}
                      <div className="font-semibold text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 当前背景色提示 */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">当前背景色</span>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ 
                        backgroundColor: showCanvasBackground ? canvasBackgroundColor : 'transparent',
                        backgroundImage: !showCanvasBackground 
                          ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                          : 'none',
                        backgroundSize: '6px 6px',
                        backgroundPosition: '0 0, 0 3px, 3px -3px, -3px 0px',
                      }}
                    />
                    <span className="text-xs text-gray-500">
                      {showCanvasBackground ? canvasBackgroundColor : '透明'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">在左侧面板修改背景颜色</p>
              </div>

              {/* 水印设置 */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.includeWatermark}
                    onChange={(e) => setOptions((prev) => ({ ...prev, includeWatermark: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-900">添加水印</span>
                </label>
                
                {options.includeWatermark && (
                  <div className="space-y-3 pl-6">
                    {/* 水印文字 */}
                    <input
                      type="text"
                      value={options.watermarkText}
                      onChange={(e) => setOptions((prev) => ({ ...prev, watermarkText: e.target.value }))}
                      placeholder="水印文字"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    
                    {/* 水印位置 */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">位置</label>
                      <div className="grid grid-cols-4 gap-1">
                        {positionOptions.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setOptions((prev) => ({ ...prev, watermarkPosition: opt.value }))}
                            className={`py-1.5 px-2 rounded-lg text-xs transition-all ${
                              options.watermarkPosition === opt.value
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* 水印透明度 */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs text-gray-500">透明度</label>
                        <span className="text-xs text-gray-500">{options.watermarkOpacity}%</span>
                      </div>
                      <input
                        type="range"
                        min={10}
                        max={80}
                        value={options.watermarkOpacity}
                        onChange={(e) => setOptions((prev) => ({ ...prev, watermarkOpacity: Number(e.target.value) }))}
                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 生成按钮 */}
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/25"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {exportStatus || '导出中...'}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    生成图片
                  </>
                )}
              </button>

              {/* 错误提示 */}
              {exportError && (
                <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-xs">{exportError}</span>
                  </div>
                </div>
              )}
            </div>

            {/* 右侧：预览区域 */}
            <div className="flex-1 p-5 bg-gray-50/50 min-h-[350px] flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">预览</h3>
                {exportedUrl && (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <Check className="w-3 h-3" />
                    生成成功
                  </span>
                )}
              </div>
              
              {/* 预览内容 */}
              <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden flex items-center justify-center p-4">
                {!exportedUrl && !isExporting && (
                  <div className="text-center text-gray-400">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    </div>
                    <p className="text-sm">点击"生成图片"预览效果</p>
                  </div>
                )}

                {isExporting && (
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 mx-auto mb-3 text-blue-500 animate-spin" />
                    <p className="text-sm text-gray-500">{exportStatus || '正在生成...'}</p>
                  </div>
                )}

                {exportedUrl && (
                  <img
                    src={exportedUrl}
                    alt="Exported poster"
                    className="max-w-full max-h-[400px] object-contain rounded-lg shadow-sm"
                  />
                )}
              </div>

              {/* 下载按钮 */}
              {exportedUrl && (
                <button
                  onClick={handleDownload}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/25"
                >
                  <Download className="w-4 h-4" />
                  下载图片
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportModal;
